/**
 * File: adminUsageService.ts
 * Description: Service for admin usage statistics API operations.
 * Author: GitHub Copilot
 * Created: 2026-10-13
 * Last Modified: 2026-10-13
 */

import { apiClient } from './apiClient';

const ADMIN_USAGE_ENDPOINT = '/api/admin/usage-statistics';
const ADMIN_FEEDBACK_DETAILS_ENDPOINT =
  '/api/admin/usage-statistics/feedback-details';

export interface UsageBreakdownItem {
  label: string;
  count: number;
}

export interface LineOfBusinessStatusBreakdownItem {
  line_of_business: string;
  count: number;
  status_breakdown: UsageBreakdownItem[];
}

export interface UsageStatisticsResponse {
  generated_at: string;
  total_users: number;
  total_projects: number;
  brownfield_projects: number;
  greenfield_projects: number;
  total_adr_count: number;
  published_projects: number;
  in_review_projects: number;
  average_project_completion_days: number;
  ado_enabled_users: number;
  billy_enabled_users: number;
  total_feedback_count: number;
  users_by_persona: UsageBreakdownItem[];
  users_by_line_of_business: UsageBreakdownItem[];
  projects_by_line_of_business: UsageBreakdownItem[];
  projects_by_line_of_business_status: LineOfBusinessStatusBreakdownItem[];
  projects_by_status: UsageBreakdownItem[];
  feedback_by_type: UsageBreakdownItem[];
  feedback_by_section: UsageBreakdownItem[];
}

export interface FeedbackDetailItem {
  id: number;
  project_id: number;
  project_title: string | null;
  section: string;
  agent: string;
  feedback_type: string | null;
  comment: string | null;
  created_at: string;
}

export interface FeedbackSectionGroup {
  section: string;
  count: number;
  items: FeedbackDetailItem[];
}

export interface FeedbackDetailsResponse {
  generated_at: string;
  total_feedback_count: number;
  sections: FeedbackSectionGroup[];
}

export class AdminUsageService {
  /**
   * Fetch admin-only usage statistics.
   * @returns Promise with aggregate usage data.
   */
  static async getUsageStatistics(): Promise<UsageStatisticsResponse> {
    return apiClient.get<UsageStatisticsResponse>(ADMIN_USAGE_ENDPOINT);
  }

  /**
   * Fetch admin-only feedback details grouped section-wise.
   * @returns Promise with section-wise feedback detail data.
   */
  static async getFeedbackDetails(): Promise<FeedbackDetailsResponse> {
    return apiClient.get<FeedbackDetailsResponse>(
      ADMIN_FEEDBACK_DETAILS_ENDPOINT,
    );
  }
}
